import React, { useContext } from 'react';
import { Box, Container, Spinner } from '@chakra-ui/react';
import { FiLock, FiUploadCloud } from 'react-icons/fi';
import { useSearchParams } from 'react-router-dom';
import Navbar from '../compnents/Navbar';
import SideBar from '../compnents/SideBar';
import Loading from '../compnents/Loading';
import AuthContext from '../context/AuthContext';
import JobProgressContext from '../context/JobProgressContext';
import EmptyStateCard from '../compnents/EmptyStateCard';
import LiveJobIdLoader from '../compnents/LiveJobIdLoader';
import UploadYourFile from './UploadYourFile';
import DashboardFree from './DashboardPages/DashboardFree';
import DashboardRMownBookPaid from './DashboardPages/DashboardRMownBookPaid';
import DashboardRMwhoToCallPaid from './DashboardPages/DashboardRMwhoToCallPaid';
import DashboardRMPriorityScores from './DashboardPages/DashboardRMPriorityScores';
import DashboardRMriskRanking from './DashboardPages/DashboardRMriskRanking';
import DashboardRMsegments from './DashboardPages/DashboardRMsegments';
import DashboardRMCustomerInsights from './DashboardPages/DashboardRMCustomerInsights';
import DashboardAdminScoresPaid from './DashboardPages/DashboardAdminScoresPaid';
import DashboardAdminBiasBenchmarPaid from './DashboardPages/DashboardAdminBiasBenchmarPaid';
import DashboardAdminCustomerInsightsPaid from './DashboardPages/DashboardAdminCustomerInsightsPaid';
import DashboardAdminPlatformImpact from './DashboardPages/DashboardAdminPlatformImpact';
import DashboardComplianceDashboardPaid from './DashboardPages/DashboardComplianceDashboardPaid';
import DashboardComplianceScoresPaid from './DashboardPages/DashboardComplianceScoresPaid';
import DashboardComplianceBiasBenchmarkPaid from './DashboardPages/DashboardComplianceBiasBenchmarkPaid';
import DashboardHeadDashboardPaid from './DashboardPages/DashboardHeadDashboardPaid';
import DashboardHeadBiasBenchmarkPaid from './DashboardPages/DashboardHeadBiasBenchmarkPaid';
import DashboardPortfolioDashboardPaid from './DashboardPages/DashboardPortfolioDashboardPaid';
import DashboardPortfolioCustomerInsightsPaid from './DashboardPages/DashboardPortfolioCustomerInsightsPaid';
import DashboardPortfolioBiasBenchmarkPaid from './DashboardPages/DashboardPortfolioBiasBenchmarkPaid';
import DashboardViewOnlyBiasBenchmarkPaid from './DashboardPages/DashboardViewOnlyBiasBenchmarkPaid';
import {
    AdminTodayView,
    ComplianceTodayView,
    ComplianceInsightDetailView,
    HeadTodayView,
    HeadInsightDetailView,
    PortfolioInsightDetailView,
    PortfolioTodayView,
    RMTodayView,
    ViewOnlyTodayView,
} from './DashboardPages/RoleTodayViews';

const dashboardMeta = {
    demo: {
        title: 'Synthetic Demo Workspace',
        description: 'Client-safe synthetic data covering the Team and Business role workflows.',
        minPlan: 'free',
    },
    admin: {
        title: 'Admin',
        description: 'Tenant control, buyer impact, customer scores, insights, and bias benchmark.',
        minPlan: 'pro',
    },
    rm: {
        title: 'Relationship Manager',
        description: 'Own book, who-to-call queue, priority scores, risk ranking, and segments.',
        minPlan: 'pro',
    },
    head: {
        title: 'Head of Advisory',
        description: 'Leadership readout for team adoption, risk trends, and behavioral benchmark.',
        minPlan: 'business',
    },
    portfolio: {
        title: 'Portfolio Adviser',
        description: 'Portfolio fit, customer insight, suitability evidence, and benchmark drift.',
        minPlan: 'business',
    },
    compliance: {
        title: 'Compliance',
        description: 'Suitability checks, evidence completeness, audit queues, and bias benchmark.',
        minPlan: 'business',
    },
    view_only: {
        title: 'View Only Dashboard',
        description: 'Read-only benchmark and data freshness views for controlled stakeholder access.',
        minPlan: 'business',
    },
};

const businessOnlyRoleLabels = {
    compliance: 'Compliance',
    head: 'Head of Advisory',
    portfolio: 'Portfolio Adviser',
    view_only: 'View Only',
};

const roleAliases = {
    portfolio: 'portfolio_adviser',
};

const canAccessRoleDashboard = (user, dashboardRole) => {
    if (!user) return false;
    // The synthetic demo workspace is client-safe sample content; every
    // authenticated tenant (free, Solo, Team, Business) can open it for buyer demos.
    if (dashboardRole === 'demo') return true;
    if (dashboardRole === 'view_only') {
        return user.subscription === 'business' && ['admin', 'view_only'].includes(user.role);
    }
    if (user.role === 'admin') {
        if (user.subscription === 'business') return true;
        if (['solo', 'pro'].includes(user.subscription)) return ['admin', 'rm'].includes(dashboardRole);
        if (user.subscription === 'free') return dashboardRole === 'demo';
    }

    const normalizedRole = roleAliases[dashboardRole] || dashboardRole;
    if (user.role !== normalizedRole) return false;

    if (['admin', 'rm'].includes(dashboardRole)) {
        return ['solo', 'pro', 'business'].includes(user.subscription);
    }

    return user.subscription === 'business';
};

const DashboardShell = ({ children }) => (
    <Box minH="100vh" display="flex" flexDirection="column">
        <Navbar />
        <Box display="flex" flex="1" bg="brand.bg" className="b2b-app-shell" minH="calc(100vh - 56px)">
            <SideBar />
            {children}
        </Box>
    </Box>
);

function RoleDashboardPage({ roleKey = 'demo' }) {
    const { token, user, setUser, isAuthInitializing } = useContext(AuthContext);
    const { jobStatus, showLiveLoader } = useContext(JobProgressContext);
    const [searchParams] = useSearchParams();
    const view = searchParams.get('view') || 'today';

    if (isAuthInitializing || !token) {
        return <Loading />;
    }

    if (user?.role === 'admin' && jobStatus.loading) {
        return <Loading />;
    }

    if (!user) {
        return (
            <Box minH="100vh" display="flex" flexDirection="column" justifyContent="center" alignItems="center">
                <Spinner size="xl" color="brand.teal" />
            </Box>
        );
    }

    if (user?.role === 'admin' && (showLiveLoader || jobStatus.hasIncompleteJobs)) {
        return (
            <DashboardShell>
                <LiveJobIdLoader />
            </DashboardShell>
        );
    }

    if (roleKey !== 'demo' && user.subscription !== 'free' && user.noOfFileUploads === 0) {
        if (user.role === 'admin') {
            return (
                <Box minH="100vh" display="flex" flexDirection="column" bg="brand.bg">
                    <Navbar />
                    <UploadYourFile setUser={setUser} token={token} />
                </Box>
            );
        }

        return (
            <DashboardShell>
                <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" flex="1" p={8}>
                    <EmptyStateCard
                        icon={FiUploadCloud}
                        title="Dashboards appear after the first dataset upload"
                        description="A firm admin needs to upload the CSV or Excel customer template before live analytics can be generated."
                        hint="For a client demo without live data, open Synthetic Demo Workspace."
                        bg="white"
                        borderColor="brand.divider"
                        titleColor="gray.700"
                        descriptionColor="gray.600"
                        hintColor="gray.500"
                        iconColor="brand.teal"
                    />
                </Box>
            </DashboardShell>
        );
    }

    if (!canAccessRoleDashboard(user, roleKey)) {
        const label = businessOnlyRoleLabels[roleKey] || dashboardMeta[roleKey]?.title || 'This dashboard';
        return (
            <DashboardShell>
                <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" flex="1" p={8}>
                    <EmptyStateCard
                        icon={FiLock}
                        title={`${label} is not included in this plan`}
                        description="Solo and Team include the Admin and Relationship Manager workflows. Head of Advisory, Portfolio Adviser, Compliance, View Only and the audit console are Business features."
                        hint="Use Synthetic Demo Workspace for buyer demos, or upgrade the tenant to Business for live role dashboards."
                        bg="white"
                        borderColor="purple.100"
                        titleColor="gray.700"
                        descriptionColor="gray.600"
                        hintColor="gray.500"
                        iconColor="purple.500"
                    />
                </Box>
            </DashboardShell>
        );
    }

    return (
        <DashboardShell>
            <Container maxW="1500px" flex="1" py={{ base: 4, md: 6 }} px={{ base: 4, md: 6 }} pb={{ base: 24, md: 6 }}>
                {renderDashboardContent(roleKey, view)}
            </Container>
        </DashboardShell>
    );
}

function renderDashboardContent(roleKey, view = 'today') {
    if (roleKey === 'demo') {
        return <DashboardFree />;
    }

    if (roleKey === 'admin') {
        if (view === 'clients') return <DashboardAdminCustomerInsightsPaid />;
        if (view === 'scores') return <DashboardAdminScoresPaid />;
        if (view === 'bias') return <DashboardAdminBiasBenchmarPaid />;
        if (view === 'impact') return <DashboardAdminPlatformImpact />; // team plans (hidden on Solo)
        return <AdminTodayView />;
    }

    if (roleKey === 'rm') {
        if (view === 'clients') return <DashboardRMCustomerInsights />;
        if (view === 'own-book') return <DashboardRMownBookPaid />;
        if (view === 'who-to-call') return <DashboardRMwhoToCallPaid />;
        if (view === 'priority') return <DashboardRMPriorityScores />;
        if (view === 'risk') return <DashboardRMriskRanking />;
        if (view === 'segments') return <DashboardRMsegments />;
        return <RMTodayView />;
    }

    if (roleKey === 'compliance') {
        if (view === 'dashboard') return <DashboardComplianceDashboardPaid />;
        if (view === 'scores') return <DashboardComplianceScoresPaid />;
        if (view === 'bias') return <DashboardComplianceBiasBenchmarkPaid />;
        if (['sla', 'evidence', 'audit-pack'].includes(view)) return <ComplianceInsightDetailView view={view} />;
        return <ComplianceTodayView />;
    }

    if (roleKey === 'head') {
        if (view === 'dashboard') return <DashboardHeadDashboardPaid />;
        if (view === 'bias') return <DashboardHeadBiasBenchmarkPaid />;
        if (['adoption', 'conversion', 'risk'].includes(view)) return <HeadInsightDetailView view={view} />;
        return <HeadTodayView />;
    }

    if (roleKey === 'portfolio') {
        if (view === 'dashboard') return <DashboardPortfolioDashboardPaid />;
        if (view === 'clients') return <DashboardPortfolioCustomerInsightsPaid />;
        if (view === 'bias') return <DashboardPortfolioBiasBenchmarkPaid />;
        if (['mismatch', 'drift', 'evidence'].includes(view)) return <PortfolioInsightDetailView view={view} />;
        return <PortfolioTodayView />;
    }

    if (view === 'bias') return <DashboardViewOnlyBiasBenchmarkPaid />;
    return <ViewOnlyTodayView />;
}

export default RoleDashboardPage;
